import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Dialog, PaginationSimple, Skeleton, Tooltip } from "@/components/ui";
import { HoloShimmer } from "@/components/Holo";
import { cn } from "@/lib/utils";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { DesignPackageDetail, DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";
import { designKeys } from "@/services/bedrock/queryKeys";
import { useDesignPackageMutations } from "../hooks/useDesignPackageMutations";
import { ASPECT_RATIO_LABELS, FORMAT_LABELS } from "../packageStatus";
import { GLOBALS_STALE_TIME } from "./InspirationStrip";
import { ASPECT_CLASS } from "./PackageCard";

export interface TemplateQuickPreviewProps {
  pkg: DesignPackageSummary | null;
  open: boolean;
  onOpenChange(open: boolean): void;
  onAdded(copy: DesignPackageDetail): void;
  onStartFromLook(pkg: DesignPackageSummary): void;
  canEdit: boolean;
}

export function madeForLine(pkg: Pick<DesignPackageSummary, "contextType" | "minReviews" | "maxReviews">): string {
  if (pkg.contextType === "player") return "player spotlights";
  if (pkg.contextType === "none") return "branded posts";
  if (pkg.minReviews === 1 && pkg.maxReviews === 1) return "client reviews · 1 review";
  return `client reviews · ${pkg.minReviews} to ${pkg.maxReviews} reviews`;
}

export function previewMetaLine(pkg: Pick<DesignPackageSummary, "isCarousel" | "slidesCount" | "aspectRatio">): string {
  const format = pkg.isCarousel ? `${FORMAT_LABELS.carousel} · ${pkg.slidesCount} slides` : FORMAT_LABELS.single;
  return `${format} · ${ASPECT_RATIO_LABELS[pkg.aspectRatio]}`;
}

const RequestText: React.FC<{ text: string }> = ({ text }) => {
  const ref = React.useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [overflowing, setOverflowing] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  return (
    <div className="flex flex-col items-start gap-1">
      <p ref={ref} className={cn("text-sm text-slate-600 whitespace-pre-line", !expanded && "line-clamp-6")}>
        {text}
      </p>
      {overflowing && !expanded && (
        <Button variant="link" size="inline" onClick={() => setExpanded(true)}>
          More
        </Button>
      )}
    </div>
  );
};

const SlidePager: React.FC<{ pkg: DesignPackageSummary; open: boolean }> = ({ pkg, open }) => {
  const [index, setIndex] = React.useState(0);
  const { data, isLoading } = useQuery({
    queryKey: designKeys.previews(pkg.id, null, false),
    queryFn: () => DesignPackageServices.previews(pkg.id, { themeIds: [null] }),
    enabled: open,
    staleTime: GLOBALS_STALE_TIME,
  });

  React.useEffect(() => setIndex(0), [pkg.id]);

  if (isLoading) return <HoloShimmer aspectRatio={pkg.aspectRatio} />;

  const previewUrls = (data?.[0]?.slides ?? []).map(slide => slide.url).filter((url): url is string => !!url);
  const urls = previewUrls.length > 0 ? previewUrls : pkg.thumbnailUrl ? [pkg.thumbnailUrl] : [];
  const current = Math.min(index, Math.max(urls.length - 1, 0));

  return (
    <div className="flex flex-col gap-2">
      {urls.length > 0 ? (
        <img
          src={urls[current]}
          alt={pkg.name}
          className={cn("w-full rounded-xl border border-slate-200 object-cover", ASPECT_CLASS[pkg.aspectRatio])}
        />
      ) : (
        <div className={cn("w-full rounded-xl bg-slate-100", ASPECT_CLASS[pkg.aspectRatio])} />
      )}
      {urls.length > 1 && (
        <PaginationSimple hideLabel page={current + 1} totalPages={urls.length} onPageChange={p => setIndex(p - 1)} />
      )}
    </div>
  );
};

export const TemplateQuickPreview: React.FC<TemplateQuickPreviewProps> = ({
  pkg,
  open,
  onOpenChange,
  onAdded,
  onStartFromLook,
  canEdit,
}) => {
  const { duplicate } = useDesignPackageMutations();
  // List summaries don't carry the original request, so the preview reads it from the detail.
  const detail = useQuery({
    queryKey: designKeys.detail(pkg?.id ?? 0),
    queryFn: () => DesignPackageServices.get(pkg!.id),
    enabled: open && pkg != null,
    staleTime: GLOBALS_STALE_TIME,
  });

  if (!pkg) return null;

  const request = detail.data?.instructions?.trim() ?? "";

  const addButton = (
    <Button
      loading={duplicate.isPending}
      disabled={!canEdit}
      onClick={() => duplicate.mutate(pkg.id, { onSuccess: added => onAdded(added) })}
    >
      Add to my designs
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={next => onOpenChange(next)} size="lg" showCloseButton>
      <Dialog.Header>
        <Dialog.Title className="sr-only">{pkg.name}</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SlidePager pkg={pkg} open={open} />
        <div className="flex flex-col gap-3 min-w-0">
          <span className="text-xl font-bold text-slate-800">{pkg.name}</span>
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-700">Made for</span> {madeForLine(pkg)}
          </div>
          <span className="text-xs text-slate-500">{previewMetaLine(pkg)}</span>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">What was asked for</span>
            {detail.isLoading ? (
              <Skeleton data-testid="request-skeleton" className="h-12 w-full" />
            ) : request ? (
              <RequestText text={request} />
            ) : (
              <p className="text-sm text-slate-400">No description was saved for this design.</p>
            )}
          </div>
          <p className="text-sm text-slate-500 bg-slate-50 rounded-lg p-3">
            Every post is personalized with the player's name, headshot, and branding.
          </p>
        </div>
      </Dialog.Content>
      <Dialog.Footer>
        {canEdit && (
          <Button variant="link" onClick={() => onStartFromLook(pkg)}>
            Start from this look
          </Button>
        )}
        {canEdit ? (
          addButton
        ) : (
          <Tooltip>
            <Tooltip.Trigger render={<span className="inline-flex" data-testid="add-disabled-wrapper" />}>
              {addButton}
            </Tooltip.Trigger>
            <Tooltip.Content>You can browse designs but not add them.</Tooltip.Content>
          </Tooltip>
        )}
      </Dialog.Footer>
    </Dialog>
  );
};
